import { useState, useEffect } from 'react'

const API_BASE_URL = 'http://localhost:3000'

// User Analytics Types
export interface UserAnalytics {
  chatMessages: number
  contentUploads: number
  lastActivity: string | null
}

// Company Analytics Types
export interface CompanyAnalytics {
  totalUsers: number
  activeUsers: number
  totalContent: number
  totalChats: number
  contentByType: Record<string, number>
  userActivity: Array<{ date: string; count: number }>
}

// Platform Analytics Types
export interface PlatformAnalytics {
  totalCompanies: number
  totalUsers: number
  totalContent: number
  totalChats: number
  usersByRole: Record<string, number>
  contentByType: Record<string, number>
  topCompanies: Array<{
    companyId: string
    companyName: string
    userCount: number
  }>
}

export interface AnalyticsDateRange {
  startDate?: string
  endDate?: string
}

interface UseAnalyticsResult<T> {
  data: T | null
  loading: boolean
  error: string | null
  refetch: () => void
}

/**
 * Hook for fetching user analytics
 * Requires authentication (company_user, company_admin, or super_admin role)
 */
export function useUserAnalytics(dateRange?: AnalyticsDateRange): UseAnalyticsResult<UserAnalytics> {
  const [data, setData] = useState<UserAnalytics | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchData = async () => {
    setLoading(true)
    setError(null)

    try {
      const token = localStorage.getItem('token')
      if (!token) {
        throw new Error('No authentication token found')
      }

      const params = new URLSearchParams()
      if (dateRange?.startDate) params.append('startDate', dateRange.startDate)
      if (dateRange?.endDate) params.append('endDate', dateRange.endDate)

      const response = await fetch(`${API_BASE_URL}/api/analytics/user?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        throw new Error('Failed to fetch user analytics')
      }

      const result = await response.json()
      if (result.success) {
        setData(result.data)
      } else {
        throw new Error(result.error || 'Failed to fetch user analytics')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
      console.error('Error fetching user analytics:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [dateRange?.startDate, dateRange?.endDate])

  return { data, loading, error, refetch: fetchData }
}

/**
 * Hook for fetching company analytics
 * Requires authentication (company_admin or super_admin role)
 * @param companyId - Optional company ID (required for super_admin)
 */
export function useCompanyAnalytics(
  companyId?: string,
  dateRange?: AnalyticsDateRange
): UseAnalyticsResult<CompanyAnalytics> {
  const [data, setData] = useState<CompanyAnalytics | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchData = async () => {
    setLoading(true)
    setError(null)

    try {
      const token = localStorage.getItem('token')
      if (!token) {
        throw new Error('No authentication token found')
      }

      const params = new URLSearchParams()
      if (dateRange?.startDate) params.append('startDate', dateRange.startDate)
      if (dateRange?.endDate) params.append('endDate', dateRange.endDate)
      if (companyId) params.append('company_id', companyId)

      const response = await fetch(`${API_BASE_URL}/api/analytics/company?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to fetch company analytics')
      }

      const result = await response.json()
      if (result.success) {
        setData(result.data)
      } else {
        throw new Error(result.error || 'Failed to fetch company analytics')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
      console.error('Error fetching company analytics:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [companyId, dateRange?.startDate, dateRange?.endDate])

  return { data, loading, error, refetch: fetchData }
}

/**
 * Hook for fetching platform analytics
 * Requires authentication (super_admin role only)
 */
export function usePlatformAnalytics(dateRange?: AnalyticsDateRange): UseAnalyticsResult<PlatformAnalytics> {
  const [data, setData] = useState<PlatformAnalytics | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchData = async () => {
    setLoading(true)
    setError(null)

    try {
      const token = localStorage.getItem('token')
      if (!token) {
        throw new Error('No authentication token found')
      }

      const params = new URLSearchParams()
      if (dateRange?.startDate) params.append('startDate', dateRange.startDate)
      if (dateRange?.endDate) params.append('endDate', dateRange.endDate)

      const response = await fetch(`${API_BASE_URL}/api/analytics/platform?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        throw new Error('Failed to fetch platform analytics')
      }

      const result = await response.json()
      if (result.success) {
        setData(result.data)
      } else {
        throw new Error(result.error || 'Failed to fetch platform analytics')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
      console.error('Error fetching platform analytics:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [dateRange?.startDate, dateRange?.endDate])

  return { data, loading, error, refetch: fetchData }
}
